import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useObservationDateQueryParam } from '../../../../../hooks';
import { Path, routeDetails } from '../../../../../router/routeDetails';
import { StopWithDetails } from '../../../../../types';
import { useWrapInContextNavigation } from '../../../../common/Forms/NavigationBlocker';
import { LoadingWrapper } from '../../../../common/Loaders';
import { Modal, ModalBody, ModalHeader } from '../../../../common/Modals';
import { EditStopForm } from './EditStopForm';
import { EditStopVersionResult } from './Types/EditStopVersionResult';
import { useResolveExistingStopValidityRanges } from './Utils';
import { useFindLinesByStopId } from './Utils/useFindLinesByStopId';

const testIds = {
  modal: 'EditStopModal::modal',
  loading: 'EditStopModal::loading',
};

type EditStopModalProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly originalStop: StopWithDetails | null;
};

export const EditStopModal: FC<EditStopModalProps> = ({
  isOpen,
  onClose,
  originalStop,
}) => {
  const { t } = useTranslation();
  const wrappedOnClose = useWrapInContextNavigation('EditStopForm')(onClose);

  const { observationDate } = useObservationDateQueryParam();

  const navigate = useNavigate();

  const { ranges, loading: loadingExistingValidityRanges } =
    useResolveExistingStopValidityRanges({
      stopPlaceId: originalStop?.stop_place_ref,
      skip: !isOpen,
    });

  const { lines, loading: loadingLinesForStop } = useFindLinesByStopId({
    stopPlaceId: originalStop?.stop_place_ref,
    validOn: observationDate,
    skip: !isOpen,
  });

  const onEditDone = (result: EditStopVersionResult) => {
    onClose();

    if (result.validityStart) {
      navigate(
        routeDetails[Path.stopDetails].getLink(originalStop?.label, {
          observationDate: result.validityStart,
          priority: result.priority,
        }),
        { replace: true },
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={wrappedOnClose}
      testId={testIds.modal}
      contentClassName="w-[585px]"
    >
      <ModalHeader
        onClose={wrappedOnClose}
        heading={t(($) => $.stopDetails.version.title.edit)}
      />
      <LoadingWrapper
        testId={testIds.loading}
        loading={
          !originalStop || loadingExistingValidityRanges || loadingLinesForStop
        }
      >
        {originalStop && (
          <ModalBody>
            <EditStopForm
              className="mt-4 border-x-0"
              existingValidityRanges={ranges}
              affectedLines={lines}
              originalStop={originalStop}
              onCancel={onClose}
              onEditDone={onEditDone}
            />
          </ModalBody>
        )}
      </LoadingWrapper>
    </Modal>
  );
};
